import { useState, useCallback, useEffect, useRef } from 'react';
import { SessionState, ChatMessage, ApiResponse, EvidencePack } from '../types';
import { apiService } from '../services/api';

// Helper function to extract congress sessions from evidence pack
const extractCongressSessions = (evidencePack: EvidencePack): number[] | null => {
  // Look for congress session info in various places in the evidence pack
  if (evidencePack.congress_sessions) {
    return evidencePack.congress_sessions;
  }
  
  if (evidencePack.metadata?.congress_sessions) {
    return evidencePack.metadata.congress_sessions;
  }
  
  if (evidencePack.data_context?.congress_filter) {
    return evidencePack.data_context.congress_filter;
  }
  
  // Try to extract from metrics or other fields
  if (evidencePack.metrics?.congress_sessions) {
    return evidencePack.metrics.congress_sessions;
  }
  
  return null;
};

const initialSessionState: SessionState = {
  sessionId: null,
  messages: [],
  analysisComplete: false,
  layoutSpec: null,
  componentsPayload: {},
  evidencePack: {},
  sessionTopic: null,
  congressSessions: null,
  isLoading: false,
  isCanceled: false,
  isCanceling: false,
};

export const useSession = () => {
  const [session, setSession] = useState<SessionState>(initialSessionState);
  const currentSessionIdRef = useRef<string | null>(null);
  const pendingRequestRef = useRef<Promise<any> | null>(null);
  const isWaitingForSessionIdRef = useRef<boolean>(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const sendMessage = useCallback(async (prompt: string) => {
    const userMessage: ChatMessage = {
      role: 'user',
      content: prompt,
      timestamp: new Date().toISOString(),
    };

    setSession((prev) => {
      console.log('🔄 Resetting canceled state from:', prev.isCanceled, 'to false');
      return {
        ...prev,
        messages: [...prev.messages, userMessage],
        isLoading: true,
        isCanceled: false, // Reset canceled state for new analysis
      };
    });

    try {
      console.log('📤 Sending message to backend with session_id:', currentSessionIdRef.current);
      
      // Mark that we're waiting for a session ID
      isWaitingForSessionIdRef.current = !currentSessionIdRef.current;
      
      // Store the pending request so we can cancel it
      const requestPromise = apiService.sendMessage({
        prompt,
        session_id: currentSessionIdRef.current,
      });
      pendingRequestRef.current = requestPromise;
      
      const response: ApiResponse = await requestPromise;
      pendingRequestRef.current = null;
      isWaitingForSessionIdRef.current = false;
      
      console.log('📥 Received response from backend:', response);

      // Store the session ID immediately for cancellation
      if (response.session_id) {
        currentSessionIdRef.current = response.session_id;
        console.log('💾 Stored session ID for cancellation:', response.session_id);
      }

      // Update session based on response
      setSession((prev) => {
        console.log('📨 Processing response, cancel state:', prev.isCanceled);
        
        // If the session was canceled, don't process the response
        if (prev.isCanceled) {
          console.log('🚫 Ignoring response because session was canceled');
          return prev;
        }
        
        const newState = {
          ...prev,
          sessionId: response.session_id,
          isLoading: false,
        };

        if (response.response_type === 'analysis_started') {
          // Analysis started in background, start polling for completion
          const startMessage: ChatMessage = {
            role: 'assistant',
            content: response.content || "Analysis started. You can cancel at any time.",
            timestamp: new Date().toISOString(),
          };

          // Start polling for completion
          const pollForCompletion = async () => {
            try {
              // Check if we've been canceled
              if (session.isCanceled) {
                console.log('🚫 Polling stopped - session was canceled');
                return;
              }

              const pollResponse = await apiService.sendMessage({
                prompt: "check_status",
                session_id: response.session_id,
              });
              
              if (pollResponse.response_type === 'full_report') {
                // Analysis completed, update session
                setSession((prev) => {
                  if (prev.isCanceled) return prev; // Don't update if canceled
                  
                  // Check if we already have a completion message for this topic
                  const hasCompletionMessage = prev.messages.some(msg => 
                    msg.role === 'assistant' && 
                    msg.content.includes('I have completed the analysis for') &&
                    msg.content.includes(pollResponse.session_topic || '')
                  );
                  
                  if (hasCompletionMessage) {
                    console.log('🚫 Skipping duplicate completion message for', pollResponse.session_topic);
                    // Just update the data without adding another message
                    const congressSessions = extractCongressSessions(pollResponse.evidence_pack || {});
                    return {
                      ...prev,
                      analysisComplete: true,
                      layoutSpec: pollResponse.layout_spec || null,
                      componentsPayload: pollResponse.components_payload || {},
                      evidencePack: pollResponse.evidence_pack || {},
                      sessionTopic: pollResponse.session_topic || null,
                      congressSessions,
                      isLoading: false,
                    };
                  }
                  
                  const confirmationMessage: ChatMessage = {
                    role: 'assistant',
                    content: `I have completed the analysis for **${pollResponse.session_topic}**. The full report is now available below.`,
                    timestamp: new Date().toISOString(),
                  };

                  const congressSessions = extractCongressSessions(pollResponse.evidence_pack || {});

                  return {
                    ...prev,
                    messages: [...prev.messages, confirmationMessage],
                    analysisComplete: true,
                    layoutSpec: pollResponse.layout_spec || null,
                    componentsPayload: pollResponse.components_payload || {},
                    evidencePack: pollResponse.evidence_pack || {},
                    sessionTopic: pollResponse.session_topic || null,
                    congressSessions,
                    isLoading: false,
                  };
                });
              } else if (pollResponse.response_type === 'canceled') {
                // Analysis was canceled
                setSession((prev) => {
                  if (prev.isCanceled) return prev; // Don't update if already canceled
                  
                  // Check if we already have a cancel message
                  const hasCancelMessage = prev.messages.some(msg => msg.isCanceled);
                  if (hasCancelMessage) {
                    return prev; // Don't add another cancel message
                  }
                  
                  const cancelMessage: ChatMessage = {
                    role: 'assistant',
                    content: "*Analysis was canceled by the user.*",
                    timestamp: new Date().toISOString(),
                    isCanceled: true,
                  };

                  return {
                    ...prev,
                    messages: [...prev.messages, cancelMessage],
                    isLoading: false,
                    isCanceled: true,
                  };
                });
              } else if (pollResponse.response_type === 'analysis_running') {
                // Still running, poll again in 2 seconds
                pollingRef.current = setTimeout(pollForCompletion, 2000);
              } else {
                // Unknown response type, poll again in 2 seconds
                pollingRef.current = setTimeout(pollForCompletion, 2000);
              }
            } catch (error) {
              console.error('Polling failed:', error);
              // Stop polling on error
            }
          };

          // Start polling after 2 seconds
          pollingRef.current = setTimeout(pollForCompletion, 2000);

          return {
            ...newState,
            messages: [...prev.messages, startMessage],
            isLoading: true, // Keep loading while polling
          };
        } else if (response.response_type === 'full_report') {
          // Check if we already have a completion message for this topic
          const hasCompletionMessage = prev.messages.some(msg => 
            msg.role === 'assistant' && 
            msg.content.includes('I have completed the analysis for') &&
            msg.content.includes(response.session_topic || '')
          );
          
          const congressSessions = extractCongressSessions(response.evidence_pack || {});
          
          if (hasCompletionMessage) {
            console.log('🚫 Skipping duplicate completion message for', response.session_topic);
            // Just update the data without adding another message
            return {
              ...newState,
              analysisComplete: true,
              layoutSpec: response.layout_spec || null,
              componentsPayload: response.components_payload || {},
              evidencePack: response.evidence_pack || {},
              sessionTopic: response.session_topic || null,
              congressSessions,
            };
          }

          const confirmationMessage: ChatMessage = {
            role: 'assistant',
            content: `I have completed the analysis for **${response.session_topic}**. The full report is now available below.`,
            timestamp: new Date().toISOString(),
          };

          return {
            ...newState,
            messages: [...prev.messages, confirmationMessage],
            analysisComplete: true,
            layoutSpec: response.layout_spec || null,
            componentsPayload: response.components_payload || {},
            evidencePack: response.evidence_pack || {},
            sessionTopic: response.session_topic || null,
            congressSessions,
          };
        } else if (response.response_type === 'canceled') {
          // Ignore canceled responses from backend since frontend already handled cancellation
          console.log('🚫 Ignoring canceled response from backend');
          return newState;
        } else if (response.response_type === 'chat_answer') {
          const assistantMessage: ChatMessage = {
            role: 'assistant',
            content: response.content || "I'm not sure how to respond.",
            timestamp: new Date().toISOString(),
          };

          // Check if session was cleared
          const sessionCleared = 
            response.content?.includes('cleared the session') || 
            response.content?.includes('cleared the previous analysis');

          return {
            ...newState,
            messages: [...prev.messages, assistantMessage],
            analysisComplete: sessionCleared ? false : prev.analysisComplete,
            layoutSpec: sessionCleared ? null : prev.layoutSpec,
            componentsPayload: sessionCleared ? {} : prev.componentsPayload,
            evidencePack: sessionCleared ? {} : prev.evidencePack,
            sessionTopic: sessionCleared ? null : prev.sessionTopic,
            congressSessions: sessionCleared ? null : prev.congressSessions,
          };
        }

        return newState;
      });
    } catch (error) {
      pendingRequestRef.current = null;
      isWaitingForSessionIdRef.current = false;
      console.error('Failed to send message:', error);
      
      setSession((prev) => {
        console.log('❌ Error occurred, checking cancel state:', prev.isCanceled);
        
        // If the session was canceled, don't add error message
        if (prev.isCanceled) {
          console.log('🚫 Ignoring error message because session was canceled');
          return prev;
        }
        
        // Also check if the last message is already a cancel message
        const lastMessage = prev.messages[prev.messages.length - 1];
        if (lastMessage && (lastMessage.isCanceled || lastMessage.content.includes('canceled'))) {
          console.log('🚫 Ignoring error message because cancel message already exists');
          return prev;
        }
        
        const errorMessage: ChatMessage = {
          role: 'assistant',
          content: 'Sorry, there was an error connecting to the backend. Please try again.',
          timestamp: new Date().toISOString(),
        };

        return {
          ...prev,
          messages: [...prev.messages, errorMessage],
          isLoading: false,
        };
      });
    }
  }, [session.sessionId, session.isLoading]);

  const clearSession = useCallback(() => {
    // Stop any ongoing polling
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
    
    setSession(initialSessionState);
    currentSessionIdRef.current = null; // Clear the ref as well
    pendingRequestRef.current = null;
    isWaitingForSessionIdRef.current = false;
  }, []);

  const cancelAnalysis = useCallback(async () => {
    if (!session.isLoading) return;

    console.log('🛑 Canceling analysis...');

    // Immediately show canceling state to user
    const cancelingMessage: ChatMessage = {
      role: 'assistant',
      content: "*Canceling analysis...*",
      timestamp: new Date().toISOString(),
      isCanceling: true,
    };

    // Update session immediately to show canceling state and hide progress
    setSession((prev) => ({
      ...prev,
      messages: [...prev.messages, cancelingMessage],
      isLoading: false, // Hide progress bars immediately
      isCanceling: true, // New state to track cancellation in progress
    }));

    // Check if we have a session ID to cancel
    if (!currentSessionIdRef.current) {
      console.log('⚠️ Cannot cancel yet - waiting for session ID from backend');
      console.log('⏳ Waiting for session ID...');
      
      // Wait a bit for the session ID to be available
      for (let i = 0; i < 10; i++) { // Wait up to 1 second
        if (currentSessionIdRef.current) {
          console.log('✅ Session ID received:', currentSessionIdRef.current);
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // If still no session ID, update message to indicate waiting
      if (!currentSessionIdRef.current) {
        setSession((prev) => ({
          ...prev,
          messages: prev.messages.map(msg => 
            msg.isCanceling 
              ? { ...msg, content: "*Waiting for analysis to start before canceling...*" }
              : msg
          ),
        }));
        
        // Wait a bit more and try again
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // If still no session ID, show final message
        if (!currentSessionIdRef.current) {
          setSession((prev) => ({
            ...prev,
            messages: prev.messages.map(msg => 
              msg.isCanceling 
                ? { ...msg, content: "*Analysis was canceled by the user.*", isCanceled: true, isCanceling: false }
                : msg
            ),
            isCanceling: false,
            isCanceled: true,
          }));
          return;
        }
      }
    }

    // Stop any ongoing polling
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
      console.log('🛑 Stopped polling');
    }

    // Try to cancel the pending request first
    if (pendingRequestRef.current) {
      console.log('🛑 Canceling pending request...');
      // Note: We can't actually cancel the fetch request, but we can ignore its response
    }

    const sessionIdToCancel = currentSessionIdRef.current;
    console.log('🛑 Canceling analysis for session:', sessionIdToCancel);

    // Clear the ref
    currentSessionIdRef.current = null;

    // Only try to cancel on backend if we have a session ID
    if (sessionIdToCancel) {
      try {
        // Call backend to cancel the analysis
        const result = await apiService.cancelAnalysis({ session_id: sessionIdToCancel });
        console.log('✅ Backend cancel response:', result);
        
        // Update message to show successful cancellation
        setSession((prev) => ({
          ...prev,
          messages: prev.messages.map(msg => 
            msg.isCanceling 
              ? { ...msg, content: "*Analysis was canceled by the user.*", isCanceled: true, isCanceling: false }
              : msg
          ),
          isCanceling: false,
          isCanceled: true,
          sessionId: null, // Clear session ID to force new session on next request
        }));
      } catch (error) {
        console.error('❌ Failed to cancel analysis:', error);
        
        // Even if backend cancel failed, show user that frontend cancellation worked
        setSession((prev) => ({
          ...prev,
          messages: prev.messages.map(msg => 
            msg.isCanceling 
              ? { ...msg, content: "*Analysis was canceled by the user.*", isCanceled: true, isCanceling: false }
              : msg
          ),
          isCanceling: false,
          isCanceled: true,
          sessionId: null,
        }));
      }
    } else {
      console.log('⚠️ No session ID to cancel on backend, but frontend cancellation completed');
      
      // Update message to show cancellation completed
      setSession((prev) => ({
        ...prev,
        messages: prev.messages.map(msg => 
          msg.isCanceling 
            ? { ...msg, content: "*Analysis was canceled by the user.*", isCanceled: true, isCanceling: false }
            : msg
        ),
        isCanceling: false,
        isCanceled: true,
        sessionId: null,
      }));
    }
  }, [session.isLoading, session.isCanceled]);

  // Reset canceled state when loading stops (but allow immediate reset for new requests)
  useEffect(() => {
    if (!session.isLoading && session.isCanceled) {
      // Reset canceled state immediately when loading stops
      setSession((prev) => ({
        ...prev,
        isCanceled: false,
      }));
    }
  }, [session.isLoading, session.isCanceled]);

  const loadDemoReport = useCallback(async (topic: string, question: string) => {
    const userMessage: ChatMessage = {
      role: 'user',
      content: question,
      timestamp: new Date().toISOString(),
    };

    setSession((prev) => ({
      ...prev,
      messages: [...prev.messages, userMessage],
      isLoading: true,
      isCanceled: false,
    }));

    try {
      console.log('📤 Loading demo report for topic:', topic);
      
      const response = await apiService.loadDemoReport(topic);
      
      if (response.status === 'loaded' && response.report_data) {
        const reportData = response.report_data;
        
        // Revert to the original completion message style
        const confirmationMessage: ChatMessage = {
          role: 'assistant',
          content: `I have completed the analysis for **${reportData.sessionTopic}**. The full report is now available below.`,
          timestamp: new Date().toISOString(),
        };

        const congressSessions = extractCongressSessions(reportData.evidencePack || {});
        // Simulate a 10s progressive loader for demo mode without blocking backend
        const startTs = Date.now();
        const MIN_DEMO_MS = 10_000;
        
        // Keep UI in loading for up to 10s total; if already took time, subtract it
        const remaining = Math.max(0, MIN_DEMO_MS - (Date.now() - startTs));
        
        // Stage a minimal progress cadence by briefly keeping isLoading true
        await new Promise((resolve) => setTimeout(resolve, remaining));

        setSession((prev) => ({
          ...prev,
          messages: [...prev.messages, confirmationMessage],
          analysisComplete: true,
          layoutSpec: reportData.layoutSpec || null,
          componentsPayload: reportData.componentsPayload || {},
          evidencePack: reportData.evidencePack || {},
          sessionTopic: reportData.sessionTopic || null,
          congressSessions,
          isLoading: false,
          sessionId: `demo-${Date.now()}`, // Generate demo session ID
        }));
      } else {
        throw new Error(response.message || 'Failed to load demo report');
      }
    } catch (error) {
      console.error('Failed to load demo report:', error);
      
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: 'Demo report not available. Falling back to regular analysis...',
        timestamp: new Date().toISOString(),
      };

      // Fall back to regular message sending
      setSession((prev) => ({
        ...prev,
        messages: [...prev.messages, errorMessage],
        isLoading: false,
      }));
      
      // Try regular analysis as fallback
      setTimeout(() => sendMessage(question), 1000);
    }
  }, [sendMessage]);

  return {
    session,
    sendMessage,
    clearSession,
    cancelAnalysis,
    loadDemoReport,
  };
};